import { V2, DUR, hoverOnly } from '../../../theme/tokens'
import { useEffect, useRef, useState } from 'react'
import { Box, Button, Collapse, List, ListItemButton, Typography } from '@mui/material'

/**
 * ConfirmOnSelectDropdown — picker with inline consequence-panel (Pattern B).
 *
 * @description
 *   A list of selectable options where options marked with a `consequence`
 *   expand in place with a "what will happen" panel + Confirm/Cancel. Options
 *   without a consequence fire immediately (no nag for safe picks). Designed
 *   as the primitive for the shared StatusDropdown (Stream 0 Phase 0.B).
 *
 * @useWhen
 *   - Status transition picker where some targets have external side effects
 *   - Action menu where one choice is safe and another pushes to Shopify/LSM
 *   - Settings selector where some options cascade changes
 *
 * @dontUseFor
 *   - Single binary action → InlineConfirmButton
 *   - Menu of >10 options → consider a BottomSheetSelect + per-option confirm
 *   - Navigation dropdown (route tabs) → UnderlineTabs
 *
 * @related
 *   - InlineConfirmButton — single CTA version
 *   - SegmentedSlider — fixed-option picker without confirm step
 *   - BottomSheetSelect — mobile-first single picker
 *
 * @reuseHints
 *   - PO status dropdown (Stream 0 Phase 0.B) — same pattern as RMA
 *   - Pricing rule activation picker (each rule has different market impact)
 *   - Fetcher config enable/disable with consequences (rate limits, quota)
 *   - B2B customer approval with per-option customer email side effects
 *
 * @props
 *   - options — [{value, label, hint?, disabled?, consequence?, bullets?,
 *                 confirmLabel?, cancelLabel?}]
 *   - value — current selected value (rendered as "(current)" tag)
 *   - onSelect — fn(opt) fires on safe pick OR consequence Confirm click;
 *                may return a Promise — disables other rows while in-flight
 *   - renderOption — (opt, state) => ReactNode; custom row content
 *   - sx — merged with the outer Box sx
 */
export default function ConfirmOnSelectDropdown({
  options = [],
  onSelect,
  value,
  renderOption,
  sx = {},
}) {
  const wrapRef = useRef(null)
  const [confirmingValue, setConfirmingValue] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!confirmingValue) return
    const onKey = (e) => { if (e.key === 'Escape') setConfirmingValue(null) }
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setConfirmingValue(null)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [confirmingValue])

  const pickImmediate = async (opt) => {
    try {
      setBusy(true)
      await Promise.resolve(onSelect?.(opt))
    } finally {
      setBusy(false)
      setConfirmingValue(null)
    }
  }

  const handleRowClick = (opt) => {
    if (opt.disabled || busy) return
    if (opt.consequence) {
      setConfirmingValue(opt.value === confirmingValue ? null : opt.value)
    } else {
      pickImmediate(opt)
    }
  }

  const handleConfirm = async (opt) => {
    await pickImmediate(opt)
  }

  return (
    <Box
      ref={wrapRef}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <List disablePadding>
        {options.map((opt) => {
          const isConfirming = opt.value === confirmingValue
          const isCurrent = opt.value === value
          return (
            <Box key={opt.value} sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 'none' },
            }}>
              <ListItemButton
                disabled={opt.disabled || (busy && !isConfirming)}
                onClick={() => handleRowClick(opt)}
                sx={{
                  py: 1.25,
                  px: 1.75,
                  transition: 'background-color 180ms ease',
                  bgcolor: (t) => isConfirming
                    ? (t.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.10)' : V2.warnSoft)
                    : 'transparent',
                  ...hoverOnly({
                    bgcolor: (t) => isConfirming
                      ? (t.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.10)' : V2.warnSoft)
                      : (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : V2.hoverTint),
                  }),
                  userSelect: 'none',
                }}
              >
                {renderOption ? renderOption(opt, { isCurrent, isConfirming }) : (
                  <>
                    <Typography sx={{ fontWeight: 500, fontSize: 13 }}>
                      {opt.label}
                      {isCurrent && (
                        <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>
                          (current)
                        </Typography>
                      )}
                    </Typography>
                    {opt.hint && (
                      <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.secondary' }}>
                        {opt.hint}
                      </Typography>
                    )}
                  </>
                )}
              </ListItemButton>

              <Collapse in={isConfirming} timeout={DUR.ui} unmountOnExit>
                <Box sx={{
                  px: 1.75,
                  py: 1.5,
                  bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.08)' : V2.warnSoft,
                  borderTop: (t) => `1px solid ${t.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.28)' : V2.warn100}`,
                  color: 'text.primary',
                }}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 600,
                    color: (t) => t.palette.mode === 'dark' ? V2.warn100 : V2.warnText,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em', mb: 0.75,
                  }}>
                    Heads up — this will happen
                  </Typography>
                  {opt.consequence && (
                    <Typography sx={{ fontSize: 13, mb: opt.bullets?.length ? 1 : 1.25, color: 'text.primary' }}>
                      {opt.consequence}
                    </Typography>
                  )}
                  {opt.bullets?.length > 0 && (
                    <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 1.25, fontSize: 13, color: 'text.primary' }}>
                      {opt.bullets.map((b, i) => <li key={i}>{b}</li>)}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small" variant="outlined" color="inherit"
                      onClick={(e) => { e.stopPropagation(); setConfirmingValue(null) }}
                      disabled={busy}
                    >
                      {opt.cancelLabel || 'Cancel'}
                    </Button>
                    <Button
                      size="small" variant="contained" color="warning"
                      onClick={(e) => { e.stopPropagation(); handleConfirm(opt) }}
                      disabled={busy}
                      sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                      autoFocus
                    >
                      {busy ? 'Working…' : (opt.confirmLabel || `Confirm — ${opt.label}`)}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          )
        })}
      </List>
    </Box>
  )
}
